import type { AuditStatus } from '@arhs-fleetback/types/entities/audits';

import {
  AUDIT_MODEL,
  SEARCH,
  AUDIT_STATUS,
  AUTHORS,
  ASSIGNEE_USERS,
  ASSIGNEE_USER_GROUPS,
  NOT_EXIST,
  START_DATE_FROM,
  START_DATE_TO,
  SCHEDULABLE_TYPE,
  SORT_DIRECTION
} from 'constants/FilterConstants';
import { DATE_TYPE } from 'constants/InterventionConstants';
import * as types from 'modules/audits/actionTypes';
import type { FetchAuditsParams } from 'modules/audits/types/dashboard';
import { filterSliceAdapter } from 'modules/global/reducers/filterSliceAdapter';
import { getPersistedFilters } from 'services/StateLoader';
import { createReducer } from 'utils/ReduxUtils';

type DateType = (typeof DATE_TYPE)[keyof typeof DATE_TYPE];

// UI filter model for the audits dashboard. `getBaseRequestParams` maps it to `FetchAuditsParams`
// (schedulableType + start date range → startDateFrom/startDateTo, AUDIT_MODEL → auditModelIds,
// auditStatus → status). Fields that keep the query shape are typed straight from `FetchAuditsParams`
// so the filter and the request stay in sync.
export type AuditDashboardFiltersReducer = {
  // `text` keeps the query shape, so it is typed straight from `FetchAuditsParams`.
  [SEARCH]: FetchAuditsParams['text'] | null;
  // FE-only shapes mapped to the query by `getBaseRequestParams`:
  [SCHEDULABLE_TYPE]: DateType; // current / planned / specific → start date range
  [SORT_DIRECTION]: 'asc' | 'desc'; // → sort direction on `startDate`
  [START_DATE_FROM]: string | null; // → startDateFrom (specific range)
  [START_DATE_TO]: string | null; // → startDateTo (specific range)
  [AUDIT_MODEL]: string[]; // model ids → joined into `auditModelIds`
  [AUDIT_STATUS]: AuditStatus[]; // → status
  [AUTHORS]: string[]; // author ids
  [ASSIGNEE_USERS]: string[]; // assigned user ids
  [ASSIGNEE_USER_GROUPS]: string[]; // assigned user group ids
  [NOT_EXIST]: string[]; // "not assigned" style negative filters
  archived: boolean;
};

const persistedFilters = getPersistedFilters()?.filters?.audits ?? {};

const emptyState: AuditDashboardFiltersReducer = {
  [SEARCH]: null,
  [SCHEDULABLE_TYPE]: DATE_TYPE.CURRENT,
  [SORT_DIRECTION]: 'desc',
  [START_DATE_FROM]: null,
  [START_DATE_TO]: null,
  [AUDIT_MODEL]: [],
  [AUDIT_STATUS]: [],
  [AUTHORS]: [],
  [ASSIGNEE_USERS]: [],
  [ASSIGNEE_USER_GROUPS]: [],
  [NOT_EXIST]: [],
  archived: false,
};

const filterAdapter = filterSliceAdapter(emptyState, persistedFilters);

export default createReducer(filterAdapter.getInitialState(), {
  [types.SET_AUDITS_DASHBOARD_FILTER]: filterAdapter.setFilter,
  [types.SET_AUDITS_DASHBOARD_FILTERS]: filterAdapter.setFilters,
  [types.CLEAR_AUDITS_DASHBOARD_FILTERS]: filterAdapter.resetFilters,
  [types.REMOVE_AUDITS_DASHBOARD_FILTER]: filterAdapter.removeFilter,
});
